import type { PipelineKind } from '../ludus-lead/types.ts'
import type {
  PipelineId,
  LeadId,
  LudusLeadId,
  ManagerId,
  StatusId,
  ContactId,
  TagId,
} from './ids.ts'

export * from './ids.ts'

export interface Pipeline {
  id: PipelineId
  name: string
  origin: PipelineOrigin
  /**
   * `null` = the native Home pipeline. Only the kind is surfaced here; the rest
   * of the binding is `ExternalConnection`, which the facade loads on demand.
   */
  adapterKind: string | null
  /** What its records are — a deal board or a board of calls and messages (ADR-0005 §4.2). */
  kind: PipelineKind
  isActive: boolean
}

/** Which side owns the pipeline's stage set. */
export type PipelineOrigin = 'native' | 'external'
export const PIPELINE_ORIGINS: readonly PipelineOrigin[] = ['native', 'external'] as const

/**
 * Canonical lifecycle phase an internal status rolls up to (ADR-0003, Axis A).
 * Adapter-agnostic; many internal statuses across pipelines map many-to-one onto
 * one phase. Closed set — growth is a deliberate ADR + migration, never tenant-editable.
 */
export type LifecyclePhase = 'intake' | 'qualification' | 'proposal' | 'won' | 'lost'
export const LIFECYCLE_PHASES: readonly LifecyclePhase[] = [
  'intake',
  'qualification',
  'proposal',
  'won',
  'lost',
] as const

/**
 * Engine role of an internal status (ADR-0003, Axis B) — what the distribution
 * engine does when a lead enters it. Orthogonal to {@link LifecyclePhase}; one of
 * each per status. `awaiting_distribution` is the distribution trigger; `terminal_*`
 * fire reaction hooks; `arrived` is a record-only technical status; `none` is inert.
 */
export type EngineRole =
  | 'arrived'
  | 'awaiting_distribution'
  | 'terminal_won'
  | 'terminal_lost'
  | 'none'
export const ENGINE_ROLES: readonly EngineRole[] = [
  'arrived',
  'awaiting_distribution',
  'terminal_won',
  'terminal_lost',
  'none',
] as const

export interface InternalStatus {
  id: StatusId
  pipelineId: PipelineId
  name: string
  /** Display order within the pipeline (`ORDER BY position ASC`). v2 schema. */
  position: number
  isActive: boolean
  /**
   * Canonical lifecycle bucket this status rolls up to (ADR-0003, Axis A).
   * `null` on an interaction pipeline (calls, messages): its statuses are
   * outcomes for people and sit on no lifecycle (D-15, D-16). A deal pipeline's
   * status always carries a phase.
   */
  lifecyclePhase: LifecyclePhase | null
  /** What distribution does when a lead enters this status (ADR-0003, Axis B). */
  engineRole: EngineRole
}

export interface CrmStatusMapping {
  pipelineId: PipelineId
  externalStatusKey: string
  internalStatusId: StatusId
}

/** How one source names a manager; a person named by two sources has two (#418). */
export interface ManagerIdentity {
  adapterKind: string
  externalRef: string
  displayName: string | null
  email: string | null
}

export interface Manager {
  id: ManagerId
  /**
   * Many-to-many membership (ADR-0002 §1). A manager can participate in
   * multiple pipelines; `pipelineIds.includes(P)` is the "is in pipeline P"
   * predicate.
   */
  pipelineIds: PipelineId[]
  firstName: string
  lastName: string
  middleName: string | null
  /** The person's own contact (#419): shown on their card, and never a customer identifier. */
  phone: string | null
  email: string | null
  /** Empty for a native manager: no source has ever named them. */
  identities: ManagerIdentity[]
  /**
   * The manager's internal telephony numbers (`110`, `102`) — what a call
   * names its manager by, and what the identity layer refuses to mistake for
   * a customer (A-25, ADR-0005 §2). Optional because Home managers predate
   * it; a mirrored manager carries the list the operator entered.
   */
  phoneExtensions?: string[]
  isActive: boolean
  isDeleted: boolean
  /** The manager this row was folded into (#420); such a row is deleted and answers for nothing. */
  mergedInto: ManagerId | null
}

export function managerRefIn(
  manager: Pick<Manager, 'identities'>,
  adapterKind: string,
): string | null {
  return manager.identities.find((i) => i.adapterKind === adapterKind)?.externalRef ?? null
}

/**
 * Display-friendly full name. Use everywhere UI needs a single-string label
 * for a manager — keeps formatting in one place when we eventually i18n it.
 */
export function managerDisplayName(
  m: Pick<Manager, 'firstName' | 'middleName' | 'lastName'>,
): string {
  return [m.firstName, m.middleName, m.lastName].filter((s): s is string => !!s).join(' ')
}

/**
 * Two-letter initials (first + last) for compact avatar labels. Middle name is
 * intentionally skipped — avatar slots are tiny and a third letter only fits
 * in `lg`. UI consumers that want a different reduction can derive their own.
 */
export function managerInitials(m: Pick<Manager, 'firstName' | 'lastName'>): string {
  return `${m.firstName.charAt(0)}${m.lastName.charAt(0)}`.toUpperCase()
}

export interface Tag {
  id: TagId
  name: string
  color: string | null
}

export interface Contact {
  id: ContactId
  firstName: string
  lastName: string
  middleName: string | null
  phone: string | null
  email: string | null
  isDeleted: boolean
  createdAt: Date
  updatedAt: Date
}

export function contactDisplayName(
  c: Pick<Contact, 'firstName' | 'middleName' | 'lastName'>,
): string {
  return [c.firstName, c.middleName, c.lastName].filter((s): s is string => !!s).join(' ')
}

export interface PriorityMatrixEntry {
  pipelineId: PipelineId
  managerId: ManagerId
  priority: number
}

export interface Lead {
  id: LeadId
  pipelineId: PipelineId
  statusId: StatusId
  assignedManagerId: ManagerId | null
  contactId: ContactId | null
  name: string
  price: number | null
  funnel: string | null
  externalRef: string | null
  tags: string[]
  /**
   * The commercial cycle this card belongs to (ADR-0005 §3), or `null` while the
   * identity layer has not placed it — which is the honest state for every card
   * written before that layer existed, and for one the linking pass has not yet
   * reached.
   *
   * The *link's* own metadata — method, confidence, who decided — deliberately
   * does not ride here. `Lead` is what a board renders, and a card page asking
   * "why is this call on this deal" reads the cycle through
   * `GET /api/ludus-leads/:id`, where the evidence lives beside the other cards
   * it is evidence about.
   */
  ludusLeadId: LudusLeadId | null
  createdAt: Date
  updatedAt: Date
  assignedAt: Date | null
}

export interface ManagerLoad {
  managerId: ManagerId
  pipelineId: PipelineId
  weightedLoad: number
  activeLeadCount: number
}

/**
 * Lead list sort axis. Maps to a `leads` column server-side:
 * `age → created_at`, `value → price`, `lastAction → updated_at`. Always paired
 * with `id` as a deterministic tiebreaker (price/updated_at are non-unique).
 */
export type LeadSortKey = 'age' | 'value' | 'lastAction'
export type SortDir = 'asc' | 'desc'

/** Bounded recent-window presets for the `created_at` filter. `all` = no cutoff. */
export type LeadPeriod = 'today' | '7d' | '30d' | 'all'

/**
 * Filter + paging contract for the leads list read (SBO-48/52). All fields
 * optional — an empty query reproduces the legacy "newest-first, all leads"
 * behaviour. Filters compose with AND; `period` becomes a server-computed
 * `created_at >=` cutoff (from request-time `now`, never `Date.now()` in core).
 */
export interface ListLeadsQuery {
  statusId?: StatusId | null
  managerIds?: ManagerId[]
  period?: LeadPeriod
  q?: string
  sort?: LeadSortKey
  dir?: SortDir
  limit?: number
  offset?: number
  /** Opaque keyset token (alternative to `offset`); takes precedence when both are set. */
  cursor?: string | null
}

/**
 * One page of a leads list. `total` drives the page-numbered UI; `nextCursor`
 * is an opaque keyset token (populated even on the offset path) so an
 * infinite-scroll surface can adopt cursors later without a contract change.
 */
export interface ListLeadsPage<T> {
  items: T[]
  total: number
  nextCursor: string | null
}

/** Same filter surface as {@link ListLeadsQuery}, minus sort/paging. */
export interface LeadStatsQuery {
  statusId?: StatusId | null
  managerIds?: ManagerId[]
  period?: LeadPeriod
  q?: string
}

/**
 * Server-computed aggregates over the filtered lead population (SBO-48/52).
 * Replaces the old client-side reductions over the full lead array so the
 * dashboard + side panels scale past tens of thousands of rows. `byStage`
 * ignores the `statusId` filter (it needs every stage's count at once).
 */
export interface LeadStats {
  total: number
  totalValue: number
  /**
   * Per-stage metrics over the filtered population, IGNORING any `statusId`
   * filter (every stage's row is needed for the dashboard at once). `medianAgeDays`
   * + `stuckCount` (age > 14d) match the console's `computeStageMetrics`; forward/
   * backward conversion stays a client-side ratio of these counts.
   */
  byStage: Array<{
    statusId: StatusId
    count: number
    totalValue: number
    medianAgeDays: number
    stuckCount: number
  }>
  /** Per-owner load, scoped by the `statusId` filter when present. */
  byOwner: Array<{ managerId: ManagerId; count: number; totalValue: number }>
  /** Per-funnel counts, scoped by the `statusId` filter when present. */
  byFunnel: Array<{ funnel: string; count: number }>
  /** Aging-bucket counts (`0-3d|4-7d|8-14d|15-30d|30d+`), scoped by `statusId` when present. */
  byAge: Array<{ bucket: string; count: number }>
}

export interface ScheduleWindow {
  startMinute: number
  endMinute: number
}

export interface ScheduleDay {
  weekday: 0 | 1 | 2 | 3 | 4 | 5 | 6
  windows: ScheduleWindow[]
}

export interface ScheduleException {
  managerId: ManagerId
  date: string
  windows: ScheduleWindow[] | null
}

export interface ManagerSchedule {
  managerId: ManagerId
  weeklyTemplate: ScheduleDay[]
  exceptions: ScheduleException[]
}

export interface DistributionContext {
  pipeline: Pipeline
  candidates: Manager[]
  matrix: PriorityMatrixEntry[]
  loads: ManagerLoad[]
  schedules: ManagerSchedule[]
  now: Date
}

export interface DistributionResult {
  managerId: ManagerId | null
  reason: 'assigned' | 'no_candidates' | 'all_off_schedule' | 'all_excluded'
  scoreboard: { managerId: ManagerId; score: number }[]
}
